import React, { useCallback, useMemo, useState } from "react"
import {
  DioboxEvent, Guest, GuestStatus, TextMessage,
} from "sharedTypes"
import Spinner from "components/elements/Spinner"
import {
  Box, Flex, Stack, Text,
} from "@chakra-ui/react"
import { useCurrentEvent } from "queries/events"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import { usePaginatedTextMessages, useTextMessage } from "queries/texts"
import { useHistory } from "react-router-dom"
import { sendTextMessage } from "api/texts"
import { getGuestPhoneNumber } from "utilities/objects"
import GuestAvatar from "components/elements/GuestAvatar"
import EmailOrTextMessageListItem from "components/EmailOrTextMessageListItem"
import Link from "components/elements/Link"
import Icon from "components/elements/Icon"
import useMergeTags from "services/useMergeTags"
import useWhatsAppTemplate from "services/useWhatsAppTemplate"
import useDefaultOptInText from "services/useDefaultOptInText"
import TextChannelIcon from "components/elements/TextChannelIcon"
import AlertBox, { AlertBoxType } from "components/elements/AlertBox/AlertBox"
import { asDate } from "utilities/strings"
import TextBubble from "modals/TextMessage/PreviewTextMessage/Phone/TextBubble"
import whatsappBg from "assets/images/phone/whatsapp-bg.webp"
import { buildResolvedVariables, getTemplateMediaUrl } from "utilities/whatsappTemplates"
import TemplateUnavailable from "components/TemplateEditor/TemplateUnavailable"
import { refreshGuest } from "context/actions"
import { useQueryClient } from "react-query"
import ScrollView from "components/layout/ScrollView"
import useFeatureFlag from "services/featureFlags"
import TextUpgradeAlert from "pages/EventShow/Texts/TextUpgradeAlert"
import NewTextMenu from "pages/EventShow/Texts/NewTextMenu"

type Props = {
  onClose: () => void
  guest: Guest
  event: DioboxEvent
}

const SendTextMessage = ({
  onClose,
  guest: {
    id,
    personInfo: {
      firstName,
      lastName,
      photoUrl,
    },
    status,
    checkedIn,
  },
  guest,
  event: {
    id: eventId,
    title,
    publicEventTitle,
    startDate,
    textsEnabled,
  },
}: Props) => {
  const { isLoading: eventIsLoading, data: currentEvent } = useCurrentEvent()
  const history = useHistory()
  const { isLoading: textMessagesAreLoading, fetchNextPage, texts } = usePaginatedTextMessages(
    currentEvent.id,
  )
  const [selectedTextMessageExternalId, setSelectedTextMessageExternalId] = useState<string>()
  const { replaceMergeTags } = useMergeTags({ eventId })
  const { whatsappMessaging: whatsAppEnabled } = useFeatureFlag()
  const queryClient = useQueryClient()

  const phoneNumberField = getGuestPhoneNumber(guest)

  const selectedMessage = selectedTextMessageExternalId && texts
    ? texts.find(({ externalId }) => externalId === selectedTextMessageExternalId)
    : undefined

  const { template: selectedTemplate, templateUnavailable } = useWhatsAppTemplate(
    selectedMessage?.whatsappContentSid,
  )

  // Without auto-opt-in active, the BE gate would silently drop a
  // single-guest unopted WhatsApp send — disable Send so the user gets
  // explicit feedback (warning rendered inside MessagePreview).
  const willSilentlyDrop = selectedMessage?.channel === "whatsapp"
    && selectedTemplate?.messageType !== "opt_in"
    && !guest.personInfo.optedInWhatsapp
    && (!currentEvent.autoOptInEnabled || !currentEvent.defaultOptInTextId)

  // Stable identity so MessagePreview's useMemo deps don't bust every render.
  const resolveMergeTags = useCallback(
    (text: string) => replaceMergeTags(
      text, publicEventTitle || title, asDate(startDate)!, guest,
    ),
    [replaceMergeTags, publicEventTitle, title, startDate, guest],
  )

  if (!texts || !phoneNumberField) {
    return null
  }

  const recipientPhone = `${phoneNumberField.value}`

  const visibleTexts = whatsAppEnabled
    ? texts
    : texts.filter((t) => t.channel !== "whatsapp")

  const handleSubmit = async () => {
    if (!selectedTextMessageExternalId) {
      return
    }

    await sendTextMessage(currentEvent.id, selectedTextMessageExternalId, [id])
    refreshGuest(queryClient, guest.id)
    history.goBack()
    onClose()
  }

  return (
    <ModalForm
      onSubmit={handleSubmit}
      submitText="Send"
      onClose={onClose}
      onBack={selectedTextMessageExternalId
        ? () => setSelectedTextMessageExternalId(undefined)
        : undefined}
      title="Send Text Message"
      size="md"
      submitIsDisabled={!textsEnabled || templateUnavailable || willSilentlyDrop}
      noPadding={!selectedTextMessageExternalId}
      hideActions={!selectedTextMessageExternalId}
    >
      {() => {
        if (eventIsLoading || textMessagesAreLoading) {
          return <Spinner heightFull />
        }

        if (visibleTexts.length === 0) {
          return (
            <Flex direction="column" justify="center" align="center" py={8}>
              <Icon icon="texts" size={12} />
              <Text fontSize="2xl" my={2}>No Available Text Messages</Text>
              <Text mb={4} px={12} textAlign="center">
                After you create at least one Text Message,
                you will be able to resend it to additional guests here.
              </Text>
              {textsEnabled ? (
                <NewTextMenu
                  button={(
                    <Link>Create New Text Message</Link>
                  )}
                />
              ) : <Box px={8}><TextUpgradeAlert /></Box>}
            </Flex>
          )
        }

        if (!selectedTextMessageExternalId) {
          return (
            <Box>
              {!textsEnabled && <Box p={8}><TextUpgradeAlert /></Box>}
              <ScrollView onContentScrollEnd={fetchNextPage}>
                {visibleTexts.map(({
                  externalId, name, sentAt, channel,
                }) => (
                  <EmailOrTextMessageListItem
                    key={externalId}
                    title={name}
                    icon={<TextChannelIcon channel={channel} size={8} />}
                    sentOn={sentAt}
                    onClick={() => setSelectedTextMessageExternalId(externalId)}
                    disableDraftItems
                  />
                ))}
              </ScrollView>
            </Box>
          )
        }

        if (!selectedMessage) {
          return null
        }

        return (
          <MessagePreview
            eventId={eventId}
            message={selectedMessage}
            firstName={firstName}
            lastName={lastName}
            photoUrl={photoUrl}
            guestStatus={status}
            checkedIn={!!checkedIn}
            recipientPhone={recipientPhone}
            optedInWhatsapp={guest.personInfo.optedInWhatsapp}
            autoOptInEnabled={currentEvent.autoOptInEnabled}
            defaultOptInTextId={currentEvent.defaultOptInTextId}
            replaceMergeTags={resolveMergeTags}
          />
        )
      }}
    </ModalForm>
  )
}

type MessagePreviewProps = {
  eventId: string
  message: TextMessage
  firstName: string
  lastName: string
  photoUrl?: string
  guestStatus: GuestStatus
  checkedIn: boolean
  recipientPhone: string
  optedInWhatsapp: boolean
  autoOptInEnabled: boolean
  defaultOptInTextId: number | null
  replaceMergeTags: (body: string) => string
}

const MessagePreview = ({
  eventId, message, firstName, lastName, photoUrl, guestStatus, checkedIn,
  recipientPhone, optedInWhatsapp, autoOptInEnabled, defaultOptInTextId,
  replaceMergeTags,
}: MessagePreviewProps) => {
  // Fetch full message detail — the paginated list may not include whatsappContentVariables
  const { data: fullMessage } = useTextMessage(eventId, message.externalId)
  const msg = fullMessage ?? message
  const {
    channel, body, whatsappContentSid, whatsappContentVariables,
  } = msg
  const {
    template,
    templateUnavailable: previewTemplateUnavailable,
  } = useWhatsAppTemplate(whatsappContentSid)

  const isWhatsApp = channel === "whatsapp"
  const isNonOptInWhatsApp = isWhatsApp && template?.messageType !== "opt_in"
  const wantsReviewMode = isNonOptInWhatsApp
    && !optedInWhatsapp
    && autoOptInEnabled
    && Boolean(defaultOptInTextId)
  // Without auto-opt-in active, the BE gate silently drops sends to
  // unopted guests — surface a warning so the user doesn't assume delivery.
  const willSilentlyDrop = isNonOptInWhatsApp
    && !optedInWhatsapp
    && (!autoOptInEnabled || !defaultOptInTextId)

  const { optInText, optInTemplate } = useDefaultOptInText(
    eventId, defaultOptInTextId, wantsReviewMode,
  )
  const reviewMode = wantsReviewMode && Boolean(optInText)

  const resolvedBody = useMemo(
    () => replaceMergeTags(body ?? ""),
    [replaceMergeTags, body],
  )

  const resolvedOptInBody = useMemo(
    () => replaceMergeTags(optInText?.body ?? ""),
    [replaceMergeTags, optInText?.body],
  )

  // Resolved variables needed for QR code media URL (GUEST_QR_CODE_IMAGE)
  const resolvedVariables = useMemo(() => {
    if (channel === "sms" || !template) return {}

    return buildResolvedVariables(
      template.variables,
      whatsappContentVariables ?? {},
      (mergeTag) => replaceMergeTags(mergeTag),
    )
  }, [channel, template, whatsappContentVariables, replaceMergeTags])

  const mediaUrl = useMemo(
    () => (template
      ? getTemplateMediaUrl(template, whatsappContentVariables, resolvedVariables)
      : undefined),
    [template, whatsappContentVariables, resolvedVariables],
  )

  // Mirror buildResolvedVariables for the opt-in template — covers the
  // future case of a per-guest media variable (e.g. GUEST_QR_CODE_IMAGE).
  const resolvedOptInVariables = useMemo(() => {
    if (!optInTemplate || !optInText) return {}
    return buildResolvedVariables(
      optInTemplate.variables,
      optInText.whatsappContentVariables ?? {},
      (mergeTag) => replaceMergeTags(mergeTag),
    )
  }, [optInTemplate, optInText, replaceMergeTags])

  const optInMediaUrl = useMemo(() => {
    if (!optInTemplate || !optInText) return undefined

    return getTemplateMediaUrl(
      optInTemplate,
      optInText.whatsappContentVariables ?? {},
      resolvedOptInVariables,
    )
  }, [optInTemplate, optInText, resolvedOptInVariables])

  if (previewTemplateUnavailable) {
    return <TemplateUnavailable />
  }

  return (
    <Stack spacing={6}>
      <Flex gap={2} alignItems="center">
        <GuestAvatar
          firstName={firstName}
          lastName={lastName}
          photoUrl={photoUrl}
          status={guestStatus}
          checkedIn={checkedIn}
        />
        <Box>
          <Text>{firstName} {lastName}</Text>
          <Text fontSize="sm" color="gray.500">{recipientPhone}</Text>
        </Box>
      </Flex>
      {reviewMode && optInText ? (
        <Stack spacing={5}>
          <Text>
            <Text as="span" fontWeight="medium">{firstName}</Text>
            {" hasn't opted in yet. They'll first receive the "}
            <Text as="span" fontWeight="medium">{`"${optInText.name}"`}</Text>
            {" message."}
          </Text>
          <BubbleFrame withBg>
            <TextBubble
              text={resolvedOptInBody}
              channel="whatsapp"
              mediaUrl={optInMediaUrl}
              buttons={optInTemplate?.buttons}
            />
          </BubbleFrame>
          <Text>
            {"If they reply YES within 48 hours, the "}
            <Text as="span" fontWeight="medium">{`"${msg.name}"`}</Text>
            {" message will be sent automatically."}
          </Text>
          <BubbleFrame withBg>
            <TextBubble
              text={resolvedBody}
              channel="whatsapp"
              mediaUrl={mediaUrl}
              buttons={template?.buttons}
            />
          </BubbleFrame>
        </Stack>
      ) : (
        <Stack spacing={4}>
          {willSilentlyDrop && (
            <AlertBox
              type={AlertBoxType.Warning}
              text={(
                <>
                  <Text as="span" fontWeight="medium">{firstName}</Text>
                  {" hasn't opted in to WhatsApp yet — this message won't be delivered. "}
                  {!autoOptInEnabled
                    ? "Enable Auto Opt-In on this event so unopted guests receive an opt-in message first, or manually send this person an opt-in message first."
                    : "Set a default opt-in message for this event so unopted guests receive it first, or manually send this person an opt-in message first."}
                </>
              )}
            />
          )}
          <Box>
            <Flex alignItems="center" gap={2} mb={2}>
              <TextChannelIcon channel={channel} size={5} />
              <Text fontWeight="semibold" fontSize="lg">
                {isWhatsApp ? "WhatsApp Message" : "SMS Message"}
              </Text>
            </Flex>
            <BubbleFrame withBg={isWhatsApp}>
              <TextBubble
                text={resolvedBody}
                channel={isWhatsApp ? "whatsapp" : "sms"}
                mediaUrl={mediaUrl}
                buttons={template?.buttons}
              />
            </BubbleFrame>
          </Box>
        </Stack>
      )}
    </Stack>
  )
}

const BubbleFrame = ({
  withBg, children,
}: { withBg: boolean, children: React.ReactNode }) => (
  <Box
    borderRadius="lg"
    p={4}
    {...(withBg ? {
      bgImage: `url(${whatsappBg})`,
      bgSize: "cover",
      bgPos: "center",
    } : {})}
  >
    {children}
  </Box>
)

export default SendTextMessage
